/** Pure helpers for the country admin's KYC review screen (#389). No React. */

import type { KycDocumentDecision, KycDocumentReviewState, KycSummary } from '@/services/brokerKyc';

export type Tone = 'default' | 'success' | 'warning' | 'error' | 'info';

export function documentStatusTone(status: string): Tone {
  switch (status) {
    case 'verified':
      return 'success';
    case 'rejected':
      return 'error';
    case 'changes_requested':
      return 'warning';
    case 'pending':
      return 'info';
    default:
      return 'default';
  }
}

export function dossierStatusTone(status: string): Tone {
  switch (status) {
    case 'verified':
      return 'success';
    case 'changes_requested':
      return 'warning';
    case 'submitted':
      return 'info';
    default:
      return 'default';
  }
}

export function documentStateLabel(state: KycDocumentReviewState['state'] | undefined): string {
  switch (state) {
    case 'changes_requested':
      return 'Waiting on the broker';
    case 'in_review':
      return 'Documents to review';
    case 'approved':
      return 'All documents approved';
    default:
      return 'No documents';
  }
}

export const DECISION_LABELS: Record<KycDocumentDecision, string> = {
  verify: 'Approve',
  reject: 'Reject',
  'request-changes': 'Request changes',
};

/** The server refuses a reject or request-changes without a reason; say so first. */
export function decisionNeedsReason(decision: KycDocumentDecision): boolean {
  return decision !== 'verify';
}

/** Mirrors the server's guard on `decision/ verified`: a submitted dossier with
 *  nothing missing and every document approved. */
export function canVerifyDossier(
  summary: Pick<KycSummary, 'kyc_status' | 'missing' | 'documents'>
): boolean {
  return (
    summary.kyc_status === 'submitted' &&
    summary.missing.length === 0 &&
    summary.documents?.state === 'approved'
  );
}

/** Why the Verify button is disabled, or null when it is not. */
export function verifyBlockedReason(
  summary: Pick<KycSummary, 'kyc_status' | 'missing' | 'documents'>
): string | null {
  if (summary.kyc_status !== 'submitted')
    return 'Only a dossier submitted by the broker can be decided.';
  if (summary.missing.length) return 'The dossier is incomplete.';
  if (summary.documents?.state !== 'approved') return 'Approve every document first.';
  return null;
}

/** #650: "<from> to <until>", or whichever end is recorded, or "Not recorded" -- never a bare "?". */
export function licenceRangeLabel(from: string | null | undefined, until: string | null | undefined): string {
  if (from && until) return `${from} to ${until}`;
  if (from) return `from ${from}`;
  if (until) return `until ${until}`;
  return 'Not recorded';
}
